import {
  createContext,
  type Dispatch,
  type SetStateAction,
  useContext,
  useEffect,
} from "react";
import type { AmpLibraryCanvasQuery } from "src/graphql";
import type { CanvasAd } from "src/pages/projects/library/canvas/canvas-ads";

export type ThreadTargets = {
  targets: ReadonlyMap<string, string[]>;
  onFocus: (nodeIds: string[]) => void;
  /** Shows the file's node, in the editor while one is open, else framed on the canvas; false when the file is not on the canvas. */
  onShowFile: (fileId: string) => boolean;
  onClearSelection: () => void;
};

export const NO_THREAD_TARGETS: ThreadTargets = {
  targets: new Map(),
  onFocus: () => {},
  onShowFile: () => false,
  onClearSelection: () => {},
};

export const CanvasThreadTargetsContext =
  createContext<ThreadTargets>(NO_THREAD_TARGETS);
export const SetCanvasThreadTargetsContext = createContext<
  Dispatch<SetStateAction<ThreadTargets | null>>
>(() => {});

/** Lets the agent chat beside the canvas show which of its threads sit on the canvas, until the canvas unmounts. */
export function useCanvasThreadTargets(value: ThreadTargets) {
  const setTargets = useContext(SetCanvasThreadTargetsContext);
  useEffect(() => {
    setTargets(value);
    return () => setTargets((current) => (current === value ? null : current));
  }, [value, setTargets]);
}

type FileNodeLike = { id: string; data: { file: { id: string } } };

/** While an editor is open, a chat's file opens in it, staying on the open copy of a file the canvas holds twice; otherwise every copy is framed. */
export function chatFileTarget<T extends FileNodeLike>(
  nodes: readonly T[],
  fileId: string,
  openNodeId: string | null,
): { kind: "open"; node: T } | { kind: "frame"; nodeIds: string[] } | null {
  const fileNodes = nodes.filter((node) => node.data.file.id === fileId);
  const [firstNode] = fileNodes;
  if (!firstNode) return null;
  if (openNodeId === null)
    return { kind: "frame", nodeIds: fileNodes.map((node) => node.id) };
  return {
    kind: "open",
    node: fileNodes.find((node) => node.id === openNodeId) ?? firstNode,
  };
}

type CanvasNodes = AmpLibraryCanvasQuery["ampCanvas"]["nodes"];
type CanvasAdSessions = ReadonlyMap<
  string,
  Pick<CanvasAd, "llm_chat_session_id" | "ad_brief">
>;

function nodesBySession(
  nodes: CanvasNodes,
  sessionsOf: (node: CanvasNodes[number]) => (string | null | undefined)[],
): ReadonlyMap<string, string[]> {
  const targets = new Map<string, string[]>();
  for (const node of nodes) {
    for (const sessionId of new Set(sessionsOf(node))) {
      if (!sessionId) continue;
      const ids = targets.get(sessionId) ?? [];
      ids.push(node.id);
      targets.set(sessionId, ids);
    }
  }
  return targets;
}

/** A generation session can produce several ads, each with its own editing session. */
export function canvasThreadTargets(
  nodes: CanvasNodes,
  ads: CanvasAdSessions,
): ReadonlyMap<string, string[]> {
  return nodesBySession(nodes, ({ file }) => {
    const ad = file?.ad ? ads.get(file.ad.assetId) : undefined;
    return [
      file?.email?.sessionId,
      file?.message?.sessionId,
      ad?.llm_chat_session_id,
      ad?.ad_brief?.llm_chat_session_id,
      ad?.ad_brief?.ad_studio_project?.llm_chat_session_id,
    ];
  });
}

/**
 * Each node's own editing session, and only one that holds a single file: a chat that wrote several emails or messages
 * can't say which it is editing, and a new file it makes lights as it generates.
 */
export function canvasEditingThreads(
  nodes: CanvasNodes,
  ads: CanvasAdSessions,
): ReadonlyMap<string, string[]> {
  const fileOf = new Map(nodes.map((node) => [node.id, node.fileId]));
  const bySession = nodesBySession(nodes, ({ file }) => {
    const ad = file?.ad ? ads.get(file.ad.assetId) : undefined;
    return [
      file?.email?.sessionId,
      file?.message?.sessionId,
      ad?.llm_chat_session_id,
    ];
  });
  return new Map(
    [...bySession].filter(
      ([, nodeIds]) => new Set(nodeIds.map((id) => fileOf.get(id))).size === 1,
    ),
  );
}

export const NO_RUNNING_THREADS: ReadonlySet<string> = new Set();

/** The chat's threads with an agent at work in them now, so the canvas can light the tiles they work on. */
export const RunningThreadsContext =
  createContext<ReadonlySet<string>>(NO_RUNNING_THREADS);
export const SetRunningThreadsContext = createContext<
  Dispatch<SetStateAction<ReadonlySet<string>>>
>(() => {});

/** Tells the canvas beside the chat which of the chat's threads are running, until the chat unmounts. */
export function useReportRunningThreads(threadIds: readonly string[]) {
  const setRunning = useContext(SetRunningThreadsContext);
  // Thread ids are UUIDs, so a space never appears in one.
  const key = [...threadIds].sort().join(" ");
  useEffect(() => {
    setRunning(key ? new Set(key.split(" ")) : NO_RUNNING_THREADS);
    return () => setRunning(NO_RUNNING_THREADS);
  }, [key, setRunning]);
}
